import React, { useEffect, useState } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import api from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { CheckCircle, DownloadSimple } from '@phosphor-icons/react';
import { downloadExport } from '@/lib/exporter';

export const FinancePage = () => {
  const { t } = useLanguage();
  const [invoices, setInvoices] = useState([]);
  const [statements, setStatements] = useState([]);
  const [pharmacies, setPharmacies] = useState([]);
  const [partners, setPartners] = useState([]);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [inv, stmt, ph, pt] = await Promise.all([
        api.get('/invoices'),
        api.get('/partner-statements'),
        api.get('/pharmacies'),
        api.get('/partners')
      ]);
      setInvoices(inv.data);
      setStatements(stmt.data);
      setPharmacies(ph.data);
      setPartners(pt.data);
    } catch (e) {
      console.error(e);
    }
  };

  const getPharmacyName = (id) => pharmacies.find(p => p.id === id)?.name || 'Unknown';
  const getPartnerName = (id) => partners.find(p => p.id === id)?.company_name || 'Unknown';

  const handleVerifyPayment = async (invoiceId) => {
    try {
      await api.post(`/invoices/${invoiceId}/verify-payment`);
      toast.success('Payment verified');
      fetchData();
    } catch (e) {
      toast.error('Verification failed');
    }
  };

  const handleMarkTransferred = async (statementId) => {
    try {
      await api.post(`/partner-statements/${statementId}/mark-transferred`);
      toast.success('Marked as transferred');
      fetchData();
    } catch (e) {
      toast.error('Failed to update');
    }
  };

  const isOverdue = (dueDate) => new Date(dueDate) < new Date();

  return (
    <div className="p-4 md:p-8 space-y-6">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <h1 className="text-4xl font-bold" style={{ fontFamily: 'Manrope', color: '#1E3A8A' }}>{t('nav.finance')}</h1>
        <div className="flex gap-2">
          <Button data-testid="export-invoices-btn" variant="outline" onClick={() => downloadExport('/exports/invoices', 'Invoices.xlsx')}><DownloadSimple size={14} className="mr-2" /> Export Invoices</Button>
          <Button data-testid="export-statements-btn" variant="outline" onClick={() => downloadExport('/exports/partner-statements', 'PartnerStatements.xlsx')}><DownloadSimple size={14} className="mr-2" /> Export Statements</Button>
        </div>
      </div>

      <Tabs defaultValue="receivables">
        <TabsList>
          <TabsTrigger value="receivables" data-testid="tab-receivables">Receivables (AR)</TabsTrigger>
          <TabsTrigger value="payables" data-testid="tab-payables">Payables (AP)</TabsTrigger>
        </TabsList>

        <TabsContent value="receivables">
          <Card className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-100">
                <tr>
                  <th className="text-left p-3">Invoice #</th>
                  <th className="text-left p-3">Pharmacy</th>
                  <th className="text-right p-3">Amount</th>
                  <th className="text-left p-3">Due Date</th>
                  <th className="text-left p-3">Status</th>
                  <th className="text-left p-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv) => (
                  <tr key={inv.id} className="border-t border-slate-200">
                    <td className="p-3 font-semibold">{inv.invoice_number}</td>
                    <td className="p-3">{getPharmacyName(inv.pharmacy_id)}</td>
                    <td className="p-3 text-right font-bold">RM {inv.total_amount?.toFixed(2)}</td>
                    <td className={`p-3 ${isOverdue(inv.due_date) && inv.status !== 'paid' ? 'text-red-600 font-bold' : ''}`}>
                      {new Date(inv.due_date).toLocaleDateString()}
                    </td>
                    <td className="p-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                        inv.status === 'paid' ? 'bg-green-100 text-green-700' :
                        inv.status === 'slip_uploaded' ? 'bg-yellow-100 text-yellow-700' :
                        isOverdue(inv.due_date) ? 'bg-red-100 text-red-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {inv.status.replace('_', ' ').toUpperCase()}
                      </span>
                    </td>
                    <td className="p-3">
                      {inv.status === 'slip_uploaded' && (
                        <div className="flex gap-1">
                          <Button
                            data-testid={`view-slip-${inv.id}`}
                            onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/invoices/${inv.id}/slip`, '_blank')}
                            size="sm"
                            variant="outline"
                          >
                            View Slip
                          </Button>
                          <Button
                            data-testid={`verify-payment-${inv.id}`}
                            onClick={() => handleVerifyPayment(inv.id)}
                            size="sm"
                            className="bg-green-600 hover:bg-green-700 text-white"
                          >
                            <CheckCircle size={14} className="mr-1" /> Verify
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        <TabsContent value="payables">
          <Card className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-100">
                <tr>
                  <th className="text-left p-3">Statement #</th>
                  <th className="text-left p-3">Partner</th>
                  <th className="text-left p-3">Period</th>
                  <th className="text-right p-3">Amount</th>
                  <th className="text-left p-3">Bank</th>
                  <th className="text-left p-3">Status</th>
                  <th className="text-left p-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {statements.map((stmt) => {
                  const partner = partners.find(p => p.id === stmt.partner_id);
                  return (
                    <tr key={stmt.id} className="border-t border-slate-200">
                      <td className="p-3 font-semibold">{stmt.statement_number}</td>
                      <td className="p-3">{getPartnerName(stmt.partner_id)}</td>
                      <td className="p-3">{String(stmt.month).padStart(2, '0')}/{stmt.year}</td>
                      <td className="p-3 text-right font-bold">RM {stmt.total_amount?.toFixed(2)}</td>
                      <td className="p-3 text-xs text-slate-600">{partner?.bank_account}</td>
                      <td className="p-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                          stmt.status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                        }`}>
                          {stmt.status.toUpperCase()}
                        </span>
                      </td>
                      <td className="p-3">
                        {stmt.status === 'pending' && (
                          <Button
                            data-testid={`mark-transferred-${stmt.id}`}
                            onClick={() => handleMarkTransferred(stmt.id)}
                            size="sm"
                            style={{ backgroundColor: '#1E3A8A' }}
                          >
                            Mark Transferred
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};
